import { ChangeDetectionStrategy, Component, ElementRef, OnChanges, OnDestroy, SimpleChanges, inject, input, viewChild, signal, effect } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { VideoTileState } from 'amazon-chime-sdk-js';
import { ChimeService } from '../chime.service';
import { ZoomControlComponent } from 'src/app/shared/components/ui/zoom-control/zoom-control.component';
import { ZoomableDirective } from 'src/app/shared/directives/zoomable.directive';

@Component({
  selector: 'app-video-tile',
  standalone: true,
  imports: [ZoomControlComponent, ZoomableDirective],
  template: `
    <div
      #zoom="appZoomable"
      appZoomable
      [enableZoom]="enableZoom()"
      [zoomSpeed]="zoomSpeed()"
      [minScale]="1.0"
      [maxScale]="4.0"
      class="video-zoom-container"
      [class.grabbing]="zoom.isDragging()"
    >
      @if (enableZoom()) {
        <app-zoom-control
          class="zoom-toolbar"
          [(zoomScale)]="zoom.zoomScale"
          [minScale]="1.0"
          [maxScale]="4.0"
          [showFullScreen]="false"
          (reset)="zoom.resetZoom()"
          (click)="$event.stopPropagation()"
        />
      }
      <div
        [class.video-zoom-content]="enableZoom()"
        [style.transform]="enableZoom() ? 'translate(' + zoom.panX() + 'px, ' + zoom.panY() + 'px) scale(' + zoom.zoomScale() + ')' : 'none'"
        [style.transition]="enableZoom() && zoom.isDragging() ? 'none' : 'transform 0.2s ease-out'"
      >
        <video #videoElement class="participant-video" [class.local-video-flipped]="isLocal()"></video>
      </div>
    </div>
  `,
  styleUrls: ['./video-tile.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(click)': 'openVideoDialog()',
    '[class.is-clickable]': 'canBeEnlarged() && !enableZoom()',
  },
})
export class VideoTileComponent implements OnChanges, OnDestroy {
  private chimeService = inject(ChimeService);
  private dialog = inject(MatDialog);

  tileState = input.required<VideoTileState>();
  isLocal = input(false);
  canBeEnlarged = input(false);
  enableZoom = input(false);
  zoomSpeed = input(0.0025);

  zoomable = viewChild(ZoomableDirective);

  constructor() {
    effect(() => {
      // Reset zoom whenever tileState changes
      const _ = this.tileState();
      this.zoomable()?.resetZoom();
    });
  }

  videoElement = viewChild.required<ElementRef<HTMLVideoElement>>('videoElement');

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['tileState']) {
      this.bindVideo();
    }
  }

  ngOnDestroy(): void {
    this.unbindVideo();
  }

  async openVideoDialog(): Promise<void> {
    if (this.enableZoom()) return;
    if (!this.canBeEnlarged() || !this.tileState()) return;

    const { VideoTileDialogComponent } = await import('../video-tile-dialog/video-tile-dialog.component');

    this.dialog.open(VideoTileDialogComponent, {
      data: { tileState: this.tileState() },
      panelClass: 'video-dialog-panel',
      width: '90vw',
      maxWidth: '90vw',
      height: '80vh',
    });
  }

  private bindVideo(): void {
    const session = this.chimeService.getMeetingSession();
    const tileState = this.tileState();
    const videoElement = this.videoElement().nativeElement;

    if (!session || !tileState.tileId) return;

    session.audioVideo.bindVideoElement(tileState.tileId, videoElement);
  }

  private unbindVideo(): void {
    const session = this.chimeService.getMeetingSession();
    const tileId = this.tileState()?.tileId;

    if (session && tileId) {
      session.audioVideo.unbindVideoElement(tileId);
    }
  }
}
